import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import { Button } from '@/admin/components/ui/button';
import { verifyLoginToken } from '@/admin/lib/api';
import { useAuth } from '@/admin/contexts/auth-context';

export const LoginConfirmPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const platform = searchParams.get('platform');

  useEffect(() => {
    // Admin platform no longer uses magic link, but keep for backward compatibility
    if (platform !== 'admin') {
      setIsLoading(false);
      return;
    }

    const bizId = searchParams.get('bizId');
    const code = searchParams.get('code');

    if (!bizId || !code) {
      setError('Missing business ID or verification code');
      setIsLoading(false);
      return;
    }

    const confirmAuth = async () => {
      try {
        const userInfo = await verifyLoginToken(bizId, code);
        login(userInfo);
        navigate('/', { replace: true });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to confirm login'
        );
      } finally {
        setIsLoading(false);
      }
    };

    confirmAuth();
  }, [searchParams, login, navigate, platform]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Confirming Login</CardTitle>
            <CardDescription>Please wait...</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Login Failed</CardTitle>
            <CardDescription>
              There was a problem confirming your login
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};
